import {
    AfterViewInit,
    Component,
    ComponentFactoryResolver,
    EventEmitter,
    Inject, OnDestroy,
    OnInit,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { ActivatedRoute, Data } from '@angular/router';
import { BreadcrumbProvider, Crumb } from '../../../breadcrumb/providers/breadcrumb-provider';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { StatsService } from '../../providers/stats.service';
import { ExercicesProvider } from '../../../exercices/providers/exercices-provider';
import moment from 'moment';
import { Moment } from 'moment';
import { AnnualPlanData } from '../../../annual-plan/merger/annual-plan-data-merger';
import { AnnualPlanService } from '../../../annual-plan/providers/annual-plans.services';
import BaseStats from '../base-stats';
import { ExercisesPerformedContainerComponent } from '../exercises-performed/exercises-performed-container.component';
import { AccentsPerformedContainerComponent } from '../accents-performed/accents-performed-container.component';
import { HoursPerAccentsPerformedContainerComponent } from '../hours-per-accents-performed/hours-per-accents-performed-container.component';
import { ConfigProvider } from "../../../config-provider.service";
import { Title } from '@angular/platform-browser';
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { SecondsToDHMPipe } from "../../../common/pipes/seconds-to-DHM-pipe";
import { User } from "../../../common/models/user";
import { Organization } from "../../../common/models/organization";
import { Accent } from "../../../common/models/stats/accent";
import { Athlete } from "../../../common/models/athlete";
import { OrganizationRole } from "../../../common/models/organization-role";
import { PageContentComponent } from "../../../common/components/page-content/page-content";
import { SideMenuComponent } from "../../../common/components/side-menu/side-menu";
import { AthleteSideMenuComponent } from "../../../athletes/components/side-menu/side-menu";
import { ModuleContentComponent } from "../../../common/components/module-content/module-content";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { HeaderComponent } from "../../../common/components/header-elitment/header-elitment";
import { FlexLayoutModule } from "@angular/flex-layout";
import { StatsRangeSelectorComponent } from "../../components/stats-range-selector.component";
import { MatButton } from "@angular/material/button";

const QUESTIONS_COMPONENTS_MAP:any =  {
    'accents-hours': HoursPerAccentsPerformedContainerComponent,
    exercises: ExercisesPerformedContainerComponent,
    accents: AccentsPerformedContainerComponent,
};

@Component({
  selector: 'app-stats-container',
  templateUrl: './stats-container.component.html',
  styleUrls: ['./stats-container.scss'],
  standalone: true,
  imports: [
    PageContentComponent,
    SideMenuComponent,
    AthleteSideMenuComponent,
    ModuleContentComponent,
    MatMenu,
    HeaderComponent,
    TranslateModule,
    FlexLayoutModule,
    StatsRangeSelectorComponent,
    MatMenuTrigger,
    MatMenuItem,
    MatButton,
  ],
  providers: [StatsService, SecondsToDHMPipe]
})
export class StatsContainerComponent implements OnInit, AfterViewInit, OnDestroy {
    public pageTitle = 'Stats';
    public instance: StatsContainerComponent = this;

    public organization: Organization = {} as Organization;
    public athleteUser: User = {} as User;
    public accents: Accent[] = [];
    public dateFrom: Moment = moment(new Date());
    public dateTo: Moment = moment(new Date());
    public annualPlanLoaded: EventEmitter<any> = new EventEmitter();
    private subscriptions: Subscription[] = [];

    @ViewChild('statsComponentRef', {
        static: false,
        read: ViewContainerRef
    })
    public statsComponentRef: ViewContainerRef = {} as ViewContainerRef;
    private factoryResolver: ComponentFactoryResolver;
    private component: BaseStats = {} as BaseStats;


    constructor(
    @Inject(ComponentFactoryResolver) factoryResolver: ComponentFactoryResolver,
                                      public conf: ConfigProvider,
                                      private title: Title,
                                      private route: ActivatedRoute,
                                      private breadcrumbProvider: BreadcrumbProvider,
                                      private translator: TranslateService,
                                      private statsProvider: StatsService,
                                      private exerciseProvider: ExercicesProvider,
                                      private annualPlanProvider: AnnualPlanService,
                                      private currentUserRoles: CurrentUserRolesProvider,
    ) {
        this.factoryResolver = factoryResolver;
    }

  public ngOnInit(): void {
    this.route.data.pipe(
      takeUntil(this.subscriptions))
      .subscribe((data: Data) => {
        this.organization = data['organization'];
        this.athleteUser = data['athlete'];

        this.loadActivePlan();
        this.createPageTitle();
        this.createBreadcrumb();
      },
    );
  }

  public ngAfterViewInit(): void {
    this.annualPlanLoaded.subscribe(() => {
      this.loadQuestionComponent();
    });
  }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    public handleDateFromEvent(date: Moment) {
        this.dateFrom = date;
        this.component.dateFrom = date;
        this.component.refreshData();
    }

    public handleDateToEvent(date: Moment) {
        this.dateTo = date;
        this.component.dateTo = date;
        this.component.refreshData();
    }

    public handleMenuChange(type: string) {
        this.loadQuestionComponent(type);
    }

    private loadQuestionComponent(type = 'accents-hours') {
        const factory = this.factoryResolver.resolveComponentFactory(QUESTIONS_COMPONENTS_MAP[type]);
        this.statsComponentRef.clear();

        const componentRef = this.statsComponentRef.createComponent(factory);
        this.component = componentRef.instance as BaseStats;
        this.component.organization = this.organization;
        this.component.athlete = this.athleteUser.athlete!;
        this.component.user = this.athleteUser;
        this.component.dateFrom = this.dateFrom;
        this.component.dateTo = this.dateTo;

        this.component.parentContainer = this;
        this.component.refreshData();
        this.component.updateTitle();
    }

    private loadActivePlan() {
        this.subscriptions.push(
            this.annualPlanProvider.findActiveByAthleteUserId(this.organization, this.athleteUser.id!, true).subscribe((data: AnnualPlanData) => {
                if (data['plan']) {
                    this.dateFrom = moment(data['plan'].startedAt);
                    this.dateTo = moment(data['plan'].startedAt).add(data['plan'].weeksLength, 'weeks');
                }

                this.annualPlanLoaded.emit();
            })
        );
    }

    private createPageTitle() {
        this.pageTitle = this.translator.instant('Stats');
        this.title.setTitle(this.pageTitle + ' | ' + this.conf.platformName);
    }

    private createBreadcrumb() {

        const crumbs: Crumb[] = [];
        if (!this.currentUserRoles.have(OrganizationRole.ID_ATHLETE, this.organization.id!)) {
            crumbs.push({
                shortTitle: 'Athletes',
                title: 'Athletes',
                url: '/' + this.organization!.code + '/athletes'
            });
        }
        crumbs.push({
            shortTitle: `${this.athleteUser.firstName?.substr(0, 1)}. ${this.athleteUser.lastName}`,
            title: `${this.athleteUser.firstName} ${this.athleteUser.lastName}`,
            url: `/${this.organization!.code}/athletes/${this.athleteUser.id}`
        });
        crumbs.push({
            shortTitle: 'Stats',
            title: 'Stats',
            url: `/${this.organization!.code}/athletes/${this.athleteUser.id}/stats`
        });
        this.breadcrumbProvider.setDefaultCrumbs(crumbs);
    }
}
